import Util from '../../../shared/util/util';
import UpdateProjectMutation from '../../../../mutations/update_project_mutation.modern';
import UpdateTaskMutation from '../../../../mutations/update_task_mutation.modern';
import UpdateProjectGroupMutation from '../../../../mutations/update_project_group_mutation';
import UpdateInitialPlanProjectMutation from '../../../../mutations/UpdateInitialPlanProjectMutation';
import {stageChangeInput} from '../../../shared/util/projectStagePicker';
import {MODAL_TYPE, showModal} from '../../../shared/components/modals/generic_modal_conductor';
import {trackEvent} from '../../../../tracking/amplitude/TrackingV2';
import {Moment} from 'moment';
import {updateProjectMutationModernMutation$data} from '../../../../mutations/__generated__/updateProjectMutationModernMutation.graphql';

type UpdateProjectPayload = NonNullable<updateProjectMutationModernMutation$data['updateProject']>;

/** The update response as a success callback reads it: the payload and its project are always there. */
type UpdateProjectResponse = {
	updateProject: UpdateProjectPayload & {project: NonNullable<UpdateProjectPayload['project']>};
};

type PhaseDates = {
	startDay?: number | null;
	startMonth?: number | null;
	startYear?: number | null;
	deadlineDay?: number | null;
	deadlineMonth?: number | null;
	deadlineYear?: number | null;
	baselineStartDay?: number | null;
	baselineStartMonth?: number | null;
	baselineStartYear?: number | null;
	baselineDeadlineDay?: number | null;
	baselineDeadlineMonth?: number | null;
	baselineDeadlineYear?: number | null;
};

export const updateProjectOnSuccess = (res: {updateProject: Pick<UpdateProjectPayload, 'errors'>}): void => {
	const errors = res.updateProject.errors;
	if (errors && errors.length === 1) {
		Util.checkForSageErrorAndShowModal(errors, false);
	}
};

export const changeName = (projectId: string, name: string): void => {
	const project = {id: projectId};

	Util.CommitMutation(
		UpdateProjectMutation,
		{
			project: project,
			name: name,
		},
		updateProjectOnSuccess
	);
};

const handleChangeClientMutationResult = (result: updateProjectMutationModernMutation$data): void => {
	const resultType = result.updateProject;
	if (resultType && resultType.errors && resultType.errors.length === 1) {
		const error = resultType.errors[0];
		if (error === 'CLIENT_CHANGE_EXPORTED_INVOICE_BILL_TO_MISMATCH') {
			showModal({
				type: MODAL_TYPE.WARNING_CLIENT_CHANGE_EXPORTED_INVOICE_BILL_TO_MISMATCH,
			});
		}
		updateProjectOnSuccess({updateProject: resultType});
	}
};

export const changeClient = (projectId: string, clientId: string | null): void => {
	const project = {id: projectId};
	Util.CommitMutation(
		UpdateProjectMutation,
		{
			project: project,
			clientId: clientId,
		},
		handleChangeClientMutationResult
	);
};

export const changeClientContact = (projectId: string, contactId: string | null): void => {
	Util.CommitMutation(
		UpdateProjectMutation,
		{
			project: {id: projectId},
			clientContactId: contactId,
		},
		updateProjectOnSuccess
	);
};

export const updateLabels = (projectId: string, newLabelIds: string[]): void => {
	Util.CommitMutation(UpdateProjectMutation, {
		project: {id: projectId},
		labels: newLabelIds,
	});
};

export const changeSageIntacctLocationEntityId = (
	projectId: string,
	sageIntacctLocationEntityId: string | null | undefined,
	{clearClient}: {clearClient?: boolean} = {}
): void => {
	const input: {project: {id: string}; sageIntacctLocationEntityId: string; clientId?: null} = {
		project: {id: projectId},
		sageIntacctLocationEntityId:
			sageIntacctLocationEntityId == null || sageIntacctLocationEntityId === '' ? '' : sageIntacctLocationEntityId,
	};
	if (clearClient) {
		input.clientId = null;
	}
	Util.CommitMutation(UpdateProjectMutation, input, clearClient ? handleChangeClientMutationResult : updateProjectOnSuccess);
};

export const changeCustomProjectId = (projectId: string, customProjectId: string): void => {
	const project = {id: projectId};
	Util.CommitMutation(
		UpdateProjectMutation,
		{
			project: project,
			customProjectId: customProjectId,
		},
		(res: UpdateProjectResponse) => {
			trackEvent('Custom Project Id', 'Changed', {value: customProjectId});
			window.history.replaceState(project, '', `/project/${res.updateProject.project.customProjectId}/settings`);
		}
	);
};

export const changeProjectColor = (projectId: string, color: string | null): void => {
	const project = {id: projectId};
	Util.CommitMutation(UpdateProjectMutation, {
		project: project,
		projectColor: color,
	});
};

export const changeDescription = (
	projectId: string,
	description: string | null | undefined,
	oldDescription: string | null | undefined
): void => {
	// Have to replace the key in the description as that seems to change between conversions
	const currentDescription = description?.replace(/"key":"[^"]*"/g, '"key":"newtext"');
	const currentOldDescription = oldDescription?.replace(/"key":"[^"]*"/g, '"key":"newtext"');
	// Skip storing if the description is the same
	if (currentOldDescription === currentDescription) return;

	const project = {id: projectId};
	Util.CommitMutation(
		UpdateProjectMutation,
		{
			project: project,
			description: description,
		},
		updateProjectOnSuccess
	);
};

export const changeStartDate = (projectId: string, projectEndDate: Moment | null, startDate: Moment | null): void => {
	const project = {id: projectId};

	if (startDate && projectEndDate && startDate.isAfter(projectEndDate)) return;
	if (startDate) {
		Util.CommitMutation(
			UpdateProjectMutation,
			{
				project: project,
				projectStartYear: startDate.year(),
				projectStartMonth: startDate.month() + 1,
				projectStartDay: startDate.date(),
			},
			updateProjectOnSuccess
		);
	} else {
		Util.CommitMutation(
			UpdateProjectMutation,
			{
				project: project,
				projectStartYear: null,
				projectStartMonth: null,
				projectStartDay: null,
				projectEndYear: null,
				projectEndMonth: null,
				projectEndDay: null,
			},
			updateProjectOnSuccess
		);
	}
};

export const changeEndDate = (projectId: string, projectStartDate: Moment | null, endDate: Moment | null): void => {
	const project = {id: projectId};

	if (projectStartDate && endDate && projectStartDate.isAfter(endDate)) return;
	Util.CommitMutation(
		UpdateProjectMutation,
		{
			project: project,
			projectEndYear: endDate ? endDate.year() : null,
			projectEndMonth: endDate ? endDate.month() + 1 : null,
			projectEndDay: endDate ? endDate.date() : null,
		},
		updateProjectOnSuccess
	);
};

export const changeStage = (projectId: string, stageId: string): void => {
	const project = {id: projectId};

	Util.CommitMutation(
		UpdateProjectMutation,
		{
			project: project,
			stageId: stageId,
		},
		updateProjectOnSuccess
	);
};

export const changeWinChance = (projectId: string, winChance: number, reopenToOpportunity = false): void => {
	if (winChance >= 0 && winChance <= 1) {
		if (reopenToOpportunity) {
			// A won opportunity is a converted project (status ≠ OPPORTUNITY); the backend reports its win
			// chance as 100% no matter what is stored, so a win-chance-only write never sticks. Re-open it to
			// the OPPORTUNITY stage first so the new win chance persists and is reported back.
			Util.CommitMutation(UpdateProjectMutation, {
				project: {id: projectId},
				...stageChangeInput('OPPORTUNITY'),
			});
		}
		Util.CommitMutation(UpdateInitialPlanProjectMutation, {
			id: projectId,
			baselineWinChance: winChance,
		});
	}
};

export const changeTasksColumn = (notDoneIds: string[], statusColumnId: string, statusColumnCategory: string): void => {
	const chunkArray: string[][] = Util.chunkArray(notDoneIds, 100);
	chunkArray.forEach(chunkIds => {
		Util.CommitMutation(UpdateTaskMutation, {
			ids: chunkIds,
			statusColumnId: statusColumnId,
			statusColumnCategory: statusColumnCategory,
			validateDependencies: false,
		});
	});
};

export const changeUseSprint = (projectId: string, useSprint: boolean): void => {
	const project = {id: projectId};

	Util.CommitMutation(UpdateProjectMutation, {
		project: project,
		sprintTimeBox: !useSprint,
	});
};

export const changeSprintLenght = (projectId: string, sprintLength: number): void => {
	if (sprintLength < 1) return;
	const project = {id: projectId};

	Util.CommitMutation(UpdateProjectMutation, {
		project: project,
		sprintLengthInDays: sprintLength,
	});
};

export const changeTaskSubLevel = (projectId: string, taskSubLevel: number): void => {
	const project = {id: projectId};

	Util.CommitMutation(UpdateProjectMutation, {
		project: project,
		taskLevels: taskSubLevel,
	});
};

export const changeTaskMonitoring = (projectId: string, option: string): void => {
	const project = {id: projectId};

	Util.CommitMutation(UpdateProjectMutation, {
		project: project,
		useTaskOwner: option === 'owner',
		useTaskFollowers: option === 'followers',
	});
};

export const changeEstimationUnit = (projectId: string, unit: string): void => {
	const project = {id: projectId};

	Util.CommitMutation(UpdateProjectMutation, {
		project: project,
		estimationUnit: unit,
	});
};

export const changeRamainingCalculations = (projectId: string, isAutomatic: boolean): void => {
	const project = {id: projectId};

	Util.CommitMutation(UpdateProjectMutation, {
		project: project,
		remainingAutoCalculated: isAutomatic,
	});
};

export const changeUsePublicCreateTask = (projectId: string, linkToPublicCreateTaskPage: string | null): void => {
	const project = {id: projectId};

	Util.CommitMutation(UpdateProjectMutation, {
		project: project,
		usePublicCreateTaskPage: linkToPublicCreateTaskPage === null,
	});
};

export const changeUsePublicCreateTaskPassword = (projectId: string, usePublicCreateTaskPassword: boolean): void => {
	const project = {id: projectId};

	Util.CommitMutation(UpdateProjectMutation, {
		project: project,
		usePublicCreateTaskPassword: !usePublicCreateTaskPassword,
	});
};

export const changePublicCreateTaskPassword = (projectId: string, password: string): void => {
	const project = {id: projectId};

	Util.CommitMutation(UpdateProjectMutation, {
		project: project,
		publicCreateTaskPassword: password,
	});
};

export const changeUseTaskHierarchy = (projectId: string, useTaskHierarchy: boolean): void => {
	const project = {id: projectId};

	Util.CommitMutation(UpdateProjectMutation, {
		project: project,
		useTaskHierarchy: useTaskHierarchy,
	});
};

export const handleChangePublicCreateTaskPageStatusColumn = (projectId: string, column: string | null): void => {
	const project = {id: projectId};

	Util.CommitMutation(UpdateProjectMutation, {
		project: project,
		publicCreateTaskPageStatusColumnId: column,
	});
};

export const changeUseBaseline = (projectId: string, useBaseline: boolean): void => {
	const project = {id: projectId};

	Util.CommitMutation(UpdateProjectMutation, {
		project: project,
		useBaseline: !useBaseline,
	});
};

export const changeSynchBaselineAndScopingDates = (
	projectId: string,
	synchBaselineAndScopingDates: boolean,
	phases: ReadonlyArray<PhaseDates>
): void => {
	const project = {id: projectId};
	const synchDates = !synchBaselineAndScopingDates;

	// If user wants to enable synching
	if (synchDates) {
		const hasPhaseWithDifferentDates = phases.some(
			phase =>
				phase.startDay !== phase.baselineStartDay ||
				phase.startMonth !== phase.baselineStartMonth ||
				phase.startYear !== phase.baselineStartYear ||
				phase.deadlineDay !== phase.baselineDeadlineDay ||
				phase.deadlineMonth !== phase.baselineDeadlineMonth ||
				phase.deadlineYear !== phase.baselineDeadlineYear
		);
		if (hasPhaseWithDifferentDates) {
			showModal({
				type: MODAL_TYPE.RELINK_BASELINE_SCOPING_DATES,
				project: project,
				synchDates: synchDates,
			});
		} else {
			Util.CommitMutation(UpdateProjectMutation, {
				project: project,
				synchBaselineAndScopingDates: !synchBaselineAndScopingDates,
			});
		}
	} else {
		Util.CommitMutation(UpdateProjectMutation, {
			project: project,
			synchBaselineAndScopingDates: !synchBaselineAndScopingDates,
		});
	}
};

export const changeUseFixedPriceForBaselineRevenue = (projectId: string, useFixedPriceForBaselineRevenue: boolean): void => {
	const project = {id: projectId};

	Util.CommitMutation(UpdateProjectMutation, {
		project: project,
		useFixedPriceForBaselineRevenue: useFixedPriceForBaselineRevenue,
	});
};

export const changeTaskCostCalculationType = (projectId: string, taskCostCalculationType: string): void => {
	const project = {id: projectId};

	Util.localStorageDeleteFincancialMap();
	Util.CommitMutation(UpdateProjectMutation, {
		project: project,
		taskCostCalculationType: taskCostCalculationType,
	});
};

export const changeUnassignedTaskHourlyCost = (projectId: string, unassignedTaskHourlyCost: number): void => {
	const project = {id: projectId};

	Util.CommitMutation(UpdateProjectMutation, {
		project: project,
		unassignedTaskHourlyCost: unassignedTaskHourlyCost,
	});
};
export const changeHoursPrStoryPoint = (projectId: string, minutes: number): void => {
	const project = {id: projectId};

	Util.CommitMutation(UpdateProjectMutation, {
		project: project,
		minutesPerEstimationPoint: minutes,
	});
};

//Project Group mutations
export const changeProjectGroupName = (projectGroupId: string, projectGroupName: string): void => {
	Util.CommitMutation(UpdateProjectGroupMutation, {
		id: projectGroupId,
		name: projectGroupName,
	});
};

export const changeProjectGroupColor = (projectGroupId: string, color: string): void => {
	Util.CommitMutation(UpdateProjectGroupMutation, {
		id: projectGroupId,
		color: color,
	});
};
export const changeProjectGroupUseSprint = (projectGroupId: string, useSprint: boolean): void => {
	Util.CommitMutation(UpdateProjectGroupMutation, {
		id: projectGroupId,
		sprintTimeBox: !useSprint,
	});
};
export const changeProjectGroupSprintLength = (projectGroupId: string, sprintLength: number): void => {
	if (sprintLength < 1) return;

	Util.CommitMutation(UpdateProjectGroupMutation, {
		id: projectGroupId,
		sprintLengthInDays: sprintLength,
	});
};

export const changeProjectGroupEstimationUnit = (projectGroupId: string, unit: string): void => {
	Util.CommitMutation(UpdateProjectGroupMutation, {
		id: projectGroupId,
		estimationUnit: unit,
	});
};
export const changeProjectGoupHoursPrStoryPoint = (projectId: string, minutes: number): void => {
	Util.CommitMutation(UpdateProjectGroupMutation, {
		id: projectId,
		minutesPerEstimationPoint: minutes,
	});
};

export const changeUseDeliverables = (projectId: string, useDeliverables: boolean): void => {
	Util.CommitMutation(UpdateProjectMutation, {
		id: projectId,
		useDeliverables: !useDeliverables,
	});
};
